import React, { useCallback, useEffect, useRef, useState } from "react";
import Sidebar, { SidebarProps } from "./Sidebar";
import ToggleSidebarButton from "../ToggleSidebarButton";

const noop = () => { /* no-op */ };

interface LayoutProps {
  children: React.ReactNode;
  sidebarContent?: React.ReactNode;
  defaultOpen?: boolean;
  onSidebarToggleClick?: (isOpen?: boolean) => void;
  isSidebarOpen?: boolean;
  isShowToggleButton?: boolean;
  size?: "sm" | "md" | "lg";
  SidebarProps?: Omit<SidebarProps, "isSidebarOpen" | "onSidebarToggleClick">;
}

export default function Layout({
  children,
  onSidebarToggleClick = noop,
  sidebarContent,
  isSidebarOpen = false,
  isShowToggleButton = true,
  SidebarProps,
}: LayoutProps) {
  const pointerEventsKillRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    pointerEventsKillRef.current = setTimeout(() => {
      // Prevent the sheet component from blocking mouse interactions
      document.body.style.pointerEvents = "";
    }, 100);
    return () => {
      if (pointerEventsKillRef.current) {
        clearTimeout(pointerEventsKillRef.current);
      }
    };
  }, [isSidebarOpen]);

  return (
    <div className="layout-root relative flex h-full w-full overflow-hidden">
      {/* Main content area */}
      <div className="flex-grow">
        {children}

        {/* Toggle button */}
        <ToggleSidebarButton
          onClick={() => onSidebarToggleClick?.(true)}
          isVisible={isShowToggleButton && !isSidebarOpen}
          label="Open sidebar"
          position="right"
          direction="left"
        />
      </div>

      {/* Sidebar */}
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        onSidebarToggleClick={onSidebarToggleClick}
        {...SidebarProps}
      >
        {sidebarContent}
      </Sidebar>
    </div>
  );
}

export const useLayout = (
  {
    isSidebarOpenInitial = false,
  }: {
    isSidebarOpenInitial?: boolean;
  } = {
    isSidebarOpenInitial: false,
  }
) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(isSidebarOpenInitial);
  const toggleSidebar = useCallback((open?: boolean) => {
    if (open !== undefined) {
      setIsSidebarOpen(open);
    } else {
      setIsSidebarOpen((prev) => !prev);
    }
  }, []);

  return { toggleSidebar, isSidebarOpen };
};
